/**
 * Pedidos (Werc 2.0 — F2) — substitui a planilha "pedidos - orders - WERC".
 * Uma aba por ano (como as abas "PO aa"), a grade com as cores (D1), o detalhe
 * num painel ao lado (redimensionável) e o rodapé do ano. Módulo manual, sem
 * ligação com estoque/importações (D3).
 */
import { createEffect, createMemo, createResource, createSignal, on, Show } from 'solid-js'
import { Button } from '@ui/components/Button'
import { Dialog } from '@ui/components/Dialog'
import { ConfirmDialog } from '@ui/components/ConfirmDialog'
import { Dropdown } from '@ui/components/Dropdown'
import { Input } from '@ui/components/Input'
import { Tabs } from '@ui/components/Tabs'
import { Resize } from '@core/component/Resize'
import { isMobileWidth } from '@core/mobile/mobileWidth'
import type { PedidoDetalhe, PedidoLinha } from '@shared/types'
import { api, versaoDados } from '../../nucleo/cliente'
import { mensagemErro } from '../../lib/erro'
import { Cadastros } from './Cadastros'
import { CompraPorCliente } from './CompraPorCliente'
import { DetalhePedido } from './DetalhePedido'
import { FormPedido } from './FormPedido'
import { Grade } from './Grade'
import { Rodape } from './Rodape'
import { Excluidos } from './Excluidos'
import { SEM_COR } from './formato'

type Vista = 'grade' | 'clientes' | 'cadastros'

const normal = (s: string) => s.normalize('NFD').replace(/\p{M}/gu, '').toLowerCase()

export function Pedidos(props: { podeEditar: boolean }) {
  const [vista, setVista] = createSignal<Vista>('grade')
  const [tick, setTick] = createSignal(0)
  const mudou = () => setTick((t) => t + 1)

  // recarrega quando chega novidade do servidor (as cores também vêm pela sincronização)
  const [status] = createResource(() => ({ v: versaoDados() }), () => api.pedidos.status())
  const [anos] = createResource(() => ({ v: versaoDados(), t: tick() }), () => api.pedidos.anos())
  const [ano, setAno] = createSignal<number | null>(null)
  createEffect(() => {
    const a = anos.latest
    if (a?.length && (ano() === null || !a.some((x) => x.ano === ano()))) setAno(a[0].ano)
  })

  const chave = () => (ano() === null ? false : { ano: ano()!, v: versaoDados(), t: tick() })
  const [grade] = createResource(chave, ({ ano }) => api.pedidos.grade(ano))
  const [resumo] = createResource(chave, ({ ano }) => api.pedidos.resumoAno(ano))

  const [busca, setBusca] = createSignal('')
  const [filtroStatus, setFiltroStatus] = createSignal<number | null>(null)
  const [selecionado, setSelecionado] = createSignal<number | null>(null)
  const [form, setForm] = createSignal<{ aberto: boolean; pedido: PedidoDetalhe | null }>({ aberto: false, pedido: null })
  const [erro, setErro] = createSignal<string | null>(null)

  // trocar de ano limpa seleção e filtros
  createEffect(on(ano, () => (setSelecionado(null), setFiltroStatus(null)), { defer: true }))

  const filtrando = () => busca().trim() !== '' || filtroStatus() !== null
  const pedidos = createMemo<PedidoLinha[]>(() => {
    const todos = grade.latest?.pedidos ?? []
    const termos = normal(busca()).split(/\s+/).filter(Boolean)
    return todos.filter((p) => {
      const f = filtroStatus()
      if (f === SEM_COR && p.statusId !== null) return false
      if (f !== null && f !== SEM_COR && p.statusId !== f) return false
      if (!termos.length) return true
      const alvo = normal([p.stat, p.cliente, p.pedido, p.orderWerc, p.proposta, p.transportadora, p.nf, p.ok, p.itens].join(' '))
      return termos.every((t) => alvo.includes(t))
    })
  })

  // ---- excluir (com confirmação) e desfazer
  const [aExcluir, setAExcluir] = createSignal<{ id: number; cliente: string | null; pedido: string | null } | null>(null)
  const [excluindo, setExcluindo] = createSignal(false)
  const [desfazer, setDesfazer] = createSignal<{ historicoId: number; texto: string } | null>(null)
  const [lixeiraAberta, setLixeiraAberta] = createSignal(false)
  let timerDesfazer: ReturnType<typeof setTimeout> | undefined
  const confirmarExclusao = async () => {
    const alvo = aExcluir()
    if (!alvo) return
    setExcluindo(true)
    try {
      const { historicoId } = await api.pedidos.excluir(alvo.id)
      setAExcluir(null)
      if (selecionado() === alvo.id) setSelecionado(null)
      mudou()
      clearTimeout(timerDesfazer)
      setDesfazer({ historicoId, texto: `Pedido de ${alvo.cliente ?? '(sem cliente)'} excluído.` })
      timerDesfazer = setTimeout(() => setDesfazer(null), 20_000)
    } catch (e) {
      avisarErro(mensagemErro(e))
    } finally {
      setExcluindo(false)
    }
  }
  const restaurar = async (historicoId: number) => {
    try {
      const { id } = await api.pedidos.restaurar(historicoId)
      setDesfazer(null)
      mudou()
      setSelecionado(id)
    } catch (e) {
      avisarErro(mensagemErro(e))
    }
  }
  const abrirEdicao = async (id: number) => {
    const d = await api.pedidos.get(id)
    if (d) setForm({ aberto: true, pedido: d })
  }

  const [exportando, setExportando] = createSignal(false)
  const exportar = async (lista: number[]) => {
    if (!lista.length) return
    setExportando(true)
    try {
      const { exportarPedidosXlsx } = await import('./exportarXlsx')
      await exportarPedidosXlsx(lista)
    } catch (e) {
      avisarErro(`Não foi possível exportar: ${mensagemErro(e)}`)
    } finally {
      setExportando(false)
    }
  }

  const avisarErro = (m: string) => {
    setErro(m)
    setTimeout(() => setErro((e) => (e === m ? null : e)), 6000)
  }

  const detalhe = (id: number) => (
    <DetalhePedido
      id={id}
      status={status()}
      podeEditar={props.podeEditar}
      tick={tick()}
      aoEditar={(d) => setForm({ aberto: true, pedido: d })}
      aoExcluir={(d) => setAExcluir({ id: d.id, cliente: d.cliente, pedido: d.pedido })}
      aoFechar={() => setSelecionado(null)}
    />
  )

  const gradeEl = () => (
    <Grade
      pedidos={pedidos()}
      fechamentos={grade.latest?.fechamentos ?? []}
      status={status()}
      mostrarFaixas={!filtrando()}
      podeEditar={props.podeEditar}
      selecionado={selecionado()}
      aoSelecionar={setSelecionado}
      aoEditar={(id) => void abrirEdicao(id)}
      aoExcluir={(p) => setAExcluir({ id: p.id, cliente: p.cliente, pedido: p.pedido })}
      aoMudar={mudou}
      aoErro={avisarErro}
      rolarParaFim={ano() ?? 0}
    />
  )

  return (
    <div class="h-full flex flex-col min-h-0">
      <div class="shrink-0 px-4 sm:px-6 pt-4 pb-3 flex flex-wrap items-center gap-3 border-b border-edge">
        <h1 class="text-xl font-semibold text-ink">Pedidos</h1>
        <Tabs
          aria-label="Vista"
          size="sm"
          value={vista()}
          onChange={setVista}
          options={[
            { value: 'grade', label: 'Pedidos do ano' },
            { value: 'clientes', label: 'Compra por cliente' },
            { value: 'cadastros', label: 'Cadastros' },
          ]}
        />
        <Show when={vista() === 'grade'}>
          <select
            class="h-8 rounded-md border border-edge bg-input px-2 text-sm text-ink"
            aria-label="Ano"
            value={ano() ?? ''}
            onChange={(e) => setAno(Number(e.currentTarget.value))}
          >
            {(anos.latest ?? []).map((a) => (
              <option value={a.ano}>
                PO {String(a.ano).slice(2)} · {a.pedidos}
              </option>
            ))}
          </select>
          <Input
            class="w-64 max-w-full"
            placeholder="Buscar cliente, pedido, NF, item…"
            value={busca()}
            onInput={(e) => setBusca(e.currentTarget.value)}
          />
          <div class="ml-auto flex items-center gap-2">
            <Dropdown>
              <Dropdown.Trigger variant="secondary" size="sm" disabled={exportando()}>
                {exportando() ? 'Exportando…' : 'Exportar Excel'}
              </Dropdown.Trigger>
              <Dropdown.Content>
                <Dropdown.Item onSelect={() => void exportar(ano() !== null ? [ano()!] : [])}>
                  Só PO {String(ano() ?? '').slice(2)}
                </Dropdown.Item>
                <Dropdown.Item onSelect={() => void exportar((anos.latest ?? []).filter((a) => a.pedidos > 0).map((a) => a.ano))}>
                  Todos os anos (uma aba por ano)
                </Dropdown.Item>
              </Dropdown.Content>
            </Dropdown>
            <Button variant="ghost" size="sm" onClick={() => setLixeiraAberta(true)} title="Pedidos excluídos (restaurar)">
              Excluídos
            </Button>
            <Show when={props.podeEditar}>
              <Button variant="accent" size="sm" onClick={() => setForm({ aberto: true, pedido: null })}>
                Novo pedido
              </Button>
            </Show>
          </div>
        </Show>
      </div>

      <Show when={erro()}>
        <div class="shrink-0 px-6 py-2 text-sm text-failure border-b border-edge" role="alert">{erro()}</div>
      </Show>
      <Show when={desfazer()}>
        {(d) => (
          <div class="shrink-0 px-6 py-2 text-sm text-ink border-b border-edge bg-hover flex items-center gap-3" role="status">
            {d().texto}
            <Button size="sm" variant="secondary" onClick={() => void restaurar(d().historicoId)}>
              Desfazer
            </Button>
            <button type="button" class="ml-auto text-xs text-ink-subtle underline" onClick={() => setDesfazer(null)}>
              fechar
            </button>
          </div>
        )}
      </Show>

      <Show
        when={vista() === 'grade'}
        fallback={
          <div class="flex-1 min-h-0 relative">
            <Show when={vista() === 'clientes'} fallback={<Cadastros podeEditar={props.podeEditar} />}>
              <CompraPorCliente anos={anos.latest ?? []} status={status()} />
            </Show>
          </div>
        }
      >
        <div class="flex-1 min-h-0 relative">
          <Show when={!isMobileWidth()} fallback={gradeEl()}>
            <Resize.Zone direction="horizontal" gutter={8} minSize={320}>
              <Resize.Panel id="ped-grade" minSize={480}>
                {gradeEl()}
              </Resize.Panel>
              <Show when={selecionado()}>
                {(id) => (
                  <Resize.Panel id="ped-detalhe" minSize={340} maxSize={720} target={{ kind: 'px', px: 420 }}>
                    {detalhe(id())}
                  </Resize.Panel>
                )}
              </Show>
            </Resize.Zone>
          </Show>
          <Show when={grade.latest && pedidos().length === 0}>
            <p class="absolute inset-x-0 top-24 text-center text-sm text-ink-subtle pointer-events-none">
              {filtrando() ? 'Nenhum pedido com esse filtro.' : 'Nenhum pedido neste ano ainda.'}
            </p>
          </Show>
        </div>
        <Show when={filtrando() && grade.latest}>
          <div class="shrink-0 px-4 py-1 text-xs text-ink-subtle border-t border-edge">
            Mostrando {pedidos().length} de {grade.latest!.pedidos.length} pedidos · os totais abaixo são do ano inteiro
          </div>
        </Show>
        <Rodape
          resumo={resumo.latest}
          status={status()}
          filtroStatus={filtroStatus()}
          aoFiltrar={setFiltroStatus}
          podeEditar={props.podeEditar}
          aoMudar={mudou}
        />
      </Show>

      {/* celular: detalhe numa gaveta */}
      <Show when={isMobileWidth()}>
        <Dialog open={selecionado() !== null} onOpenChange={(o) => !o && setSelecionado(null)} class="w-full">
          <div class="h-[80dvh]">
            <Show when={selecionado()}>{(id) => detalhe(id())}</Show>
          </div>
        </Dialog>
      </Show>

      <ConfirmDialog
        open={aExcluir() !== null}
        onOpenChange={(o) => !o && setAExcluir(null)}
        title="Excluir este pedido?"
        body={aExcluir() ? `${aExcluir()!.cliente ?? 'Pedido'}${aExcluir()!.pedido ? ` · ${aExcluir()!.pedido}` : ''}. Dá para desfazer logo depois, ou restaurar em "Excluídos".` : ''}
        confirmLabel="Excluir"
        tone="danger"
        pending={excluindo()}
        onConfirm={() => void confirmarExclusao()}
      />
      <Excluidos aberto={lixeiraAberta()} podeEditar={props.podeEditar} aoFechar={() => setLixeiraAberta(false)} aoRestaurar={(h) => void restaurar(h)} />

      <FormPedido
        aberto={form().aberto}
        ano={ano() ?? new Date().getFullYear()}
        pedido={form().pedido}
        status={status()}
        aoFechar={() => setForm({ aberto: false, pedido: null })}
        aoSalvar={(id) => (setSelecionado(id), mudou())}
      />
    </div>
  )
}
